/**
 * InformationContainer
 * --
 */
import React from 'react';
import InformationPresenter from './InformationPresenter';
import {
  // useUserAction,
  useUserStack,
} from 'store';
import {
  useGetShopInformation,
  useUpdateShopInformation,
} from 'hooks/ShopInformationHooks';
import { useGetShopInformationFiles } from 'hooks/ShopInformationFileHooks';
import MessageAlert from 'utils';

const SHOP_INFORMATION_INITIAL_VALUE = {
  shop_information_name: '',
  shop_information_content: '',
  shop_information_postcode: '',
  shop_information_address: '',
  shop_information_address_detail: '',
  application_status: 'APPLY',
  correction_status: 'APPLY',
  edit_information: null,
  latitude: null,
  longitude: null,
  type: 'SHOP',
  organization_id: null,
  commission_rate: 0,
  store_status: 'PRE_OPEN',
  vd_poss_account: null,
  toilet_information: null,
  wifi_information: null,
  is_brand: 'N',
};
const MALL_INFORMATION_INITIAL_VALUE = {
  store_num: '',
  business_num: '',
  store_owner: '',
  bank_name: '',
  account_number: '',
};
const STORE_INFORMATION_INITIAL_VALUE = {
  store_num: '',
  business_num: '',
  business_hour: '',
  services: {
    GROUP: false, // 단체 이용가능여부
    WIFI: false, // 무선 인터넷 사용 여부
    PARKING: false, // 주차 가능 여부
    RESTROOM: false, // 남녀 화장실 구분 여부
    PACKAGING: false, // 포장 가능 여부
    WHEELCHAIR: false, // 출입구 휠체어 이용 가능 여부
  },
  store_owner: '',
  receipt_information: '',
};
const INITIAL_FILES_VALUE = {
  THUMBNAIL: null,
  STORE: null,
  LICENSE: null,
};

const InformationContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { shopId = null, direction = 'horizontal', refetch } = props;

  /* ===== Mutate ===== */
  const { updateShopInformation } = useUpdateShopInformation();

  /* ===== Store ===== */
  const userInfo = useUserStack();
  // const userAction = useUserAction();

  const shopIdx = React.useMemo(() => {
    const idx = shopId ? shopId : userInfo?.shop_information_id;

    return idx;
  }, [shopId, userInfo]);

  /* ===== Data ===== */
  const { data: Shop } = useGetShopInformation(shopIdx, null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return null;

      return data;
    },
    initialData: null,
  });

  const { data: Files } = useGetShopInformationFiles(
    {
      shop_information_id: shopIdx,
    },
    {
      enabled: !!shopIdx,
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      initialData: [],
    }
  );

  /* ===== STATE ===== */
  const [shopInformation, setShopInformation] = React.useState(
    SHOP_INFORMATION_INITIAL_VALUE
  );
  const [mallInformation, setMallInformation] = React.useState(
    MALL_INFORMATION_INITIAL_VALUE
  );
  const [storeInformation, setStoreInformation] = React.useState(
    STORE_INFORMATION_INITIAL_VALUE
  );
  const [files, setFiles] = React.useState(INITIAL_FILES_VALUE);

  const [isLoading, setIsLoading] = React.useState(false);
  // 업체 타입 (ALL, SHOP, STORE)
  const [shopType, setShopType] = React.useState('SHOP');

  /* ===== VARIABLE ===== */
  const setAction = {
    SHOP: setShopInformation,
    MALL: setMallInformation,
    STORE: setStoreInformation,
    FILE: setFiles,
  };

  /* ===== FUNCTION ===== */
  /**
   * 데이터 바인딩
   * --
   * @param {string} target
   * @param {string} key
   * @param {string} value
   */
  const handleChange = (target, key, value) => {
    const targetKey = target.toLocaleUpperCase();

    setAction[targetKey]((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  /**
   * 유효성 검사
   * --
   * @returns {boolean}
   */
  const handleValidateCheck = () => {
    // SECTION: 기본정보 유효성 검사
    const {
      shop_information_name,
      shop_information_content,
      shop_information_postcode,
      shop_information_address,
      shop_information_address_detail,
    } = shopInformation;

    if (!shop_information_name) {
      MessageAlert.info('업체명은 필수정보 입니다!');
      return false;
    }
    if (!shop_information_content) {
      MessageAlert.info('업체 설명은 필수정보 입니다!');
      return false;
    }
    if (!shop_information_postcode) {
      MessageAlert.info('우편번호는 필수정보 입니다!');
      return false;
    }
    if (!shop_information_address) {
      MessageAlert.info('주소는 필수정보 입니다!');
      return false;
    }
    if (!shop_information_address_detail) {
      MessageAlert.info('상세주소는 필수정보 입니다!');
      return false;
    }

    // SECTION: 업체 기본정보 유효성 검사
    if (shopType === 'SHOP') {
      const {
        store_num,
        business_num,
        store_owner,
        bank_name,
        account_number,
      } = mallInformation;

      if (!store_num) {
        MessageAlert.info('대표번호는 필수정보 입니다!');
        return false;
      }

      if (!business_num) {
        MessageAlert.info('사업자등록번호는 필수정보 입니다!');
        return false;
      }

      if (!store_owner) {
        MessageAlert.info('대표자명은 필수정보 입니다!');
        return false;
      }

      if (!bank_name) {
        MessageAlert.info('입금은행은 필수정보 입니다!');
        return false;
      }

      if (!account_number) {
        MessageAlert.info('입금계좌는 필수정보 입니다!');
        return false;
      }

      const { LICENSE } = files;

      if (!LICENSE) {
        MessageAlert.info('사업자등록증 첨부는 필수 입니다!');
        return false;
      }
    }

    // SECTION: 매장 기본정보 유효성 검사
    if (shopType === 'STORE') {
    }

    return true;
  };

  /**
   * 업체 정보 수정
   * --
   */
  const handleSubmit = async () => {
    if (!handleValidateCheck()) return;

    const confirm = window.confirm('입력하신 정보를 저장하시겠습니까?');

    if (!confirm) return;

    try {
      setIsLoading(true);

      const updateData = {
        shop_information_id: shopIdx,
        ...shopInformation,
        mall_information: mallInformation,
        store_information: storeInformation,
      };

      const { status, data } = await updateShopInformation(updateData);

      if (status !== 200) {
        MessageAlert.info('입력하신 정보를 저장하는데 실패하였습니다.');
        return;
      }

      const { store_information, mall_information, ...rest } = data;

      setShopInformation(rest);
      setMallInformation(mall_information);
      setStoreInformation(store_information);

      // 수정 정보 저장
      // userAction.setUser({
      //   ...userInfo,
      //   ...data,
      // });

      if (refetch) refetch(data, files);

      MessageAlert.success('성공적으로 수정되었습니다.');
    } catch (err) {
      console.error('[handleSubmit] Error!! ', err);
      MessageAlert.error('정보 수정에 실패하였습니다!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 업체 정보
   * --
   */
  React.useEffect(() => {
    if (!Shop) return;

    const { store_information, mall_information, ...rest } = Shop;

    setShopInformation(rest);
    setMallInformation(mall_information);
    setStoreInformation(store_information);
    setShopType(rest.type);
  }, [Shop]);

  /**
   * 파일 정보
   * --
   */
  React.useEffect(() => {
    if (!Files.length) return;

    const newFiles = Files.reduce((acc, cur) => {
      const { type } = cur;

      acc[type] = cur;

      return acc;
    }, INITIAL_FILES_VALUE);

    setFiles(newFiles);
  }, [Files]);

  /* ===== RENDER ===== */
  return (
    <>
      <InformationPresenter
        shopId={shopIdx}
        shopInformation={shopInformation}
        mallInformation={mallInformation}
        storeInformation={storeInformation}
        shopType={shopType}
        isLoading={isLoading}
        files={files}
        direction={direction}
        onChange={handleChange}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default InformationContainer;
